

import { Legacy, API_V2 } from '../../lib/axios';
import { ICampaign } from '@clickster/constant-definitions/dist-esm/entities/campaign/types';
import { SERVERLESS } from '../../lib/axios/instances/Serverless';
import chunk from 'lodash/chunk';
import PromisePool from '@supercharge/promise-pool';
import { IAd } from '@clickster/constant-definitions/dist-esm/entities/ad/types';
import ICpcUpdateProcess from '@/src/interfaces/general/api';
import CampaignEditingService from '../pubnub/campaign-editing-service';
import { fetchAllPaginated } from '../utils/fetch-all-paginated';

const CAMPAIGNS_ENDPOINT = '/campaigns';

const conversionsUpdate = async ({
  data,
  forcePixelFiring,
}: {
  data: string | undefined;
  forcePixelFiring: boolean;
}) => {
  const response = await API_V2.post('/interactions/process-conversions', {
    data,
  });

  return response.data;
};

const create = async (data: ICampaign, params = {}) => {
  const response = await API_V2.post<ICampaign>(CAMPAIGNS_ENDPOINT, data);

  return response.data;
};

const list = async (params = {}, signal?: AbortSignal) => {
  return fetchAllPaginated<ICampaign>(API_V2, CAMPAIGNS_ENDPOINT, { populate: 'vertical', ...params }, { signal });
};

const getById = async (id: string, params = {}) => {
  const response = await API_V2.get<ICampaign>(`${CAMPAIGNS_ENDPOINT}/${id}`, {
    params: { populate: 'vertical', ...params },
  });

  return response.data;
};

const update = async (data: ICampaign, params: Record<string, any> = {}, includeTarget = true) => {
  const requestParams = { ...params };

  if (includeTarget) {
    if (!('target' in requestParams)) {
      requestParams.target = 'update';
    }
  } else {
    delete requestParams.target;
  }

  const response = await SERVERLESS.put(`/campaigns/${data._id}`, data, {
    params: requestParams,
  });

  // Publish campaign update message to PubNub
  try {
    const campaignEditingService = CampaignEditingService.getInstance();
    await campaignEditingService.publishCampaignUpdated(data._id);
  } catch (error) {
    console.error('[CampaignService] Failed to publish campaign update message:', error);
    // Don't throw error to avoid breaking the campaign update flow
  }

  return response.data;
};

const updateByAPIV2 = async (
  data: ICampaign,
  params: { [key: string]: string | number | boolean } = {},
  includeTarget = true,
) => {
  const requestParams = { ...params };

  if (includeTarget) {
    if (!('target' in requestParams)) {
      requestParams.target = 'update';
    }
  } else {
    delete requestParams.target;
  }

  const response = await API_V2.put<ICampaign>(`${CAMPAIGNS_ENDPOINT}/${data._id}`, data, {
    params: requestParams,
  });

  // Publish campaign update message to PubNub
  try {
    const campaignEditingService = CampaignEditingService.getInstance();
    await campaignEditingService.publishCampaignUpdated(data._id);
  } catch (error) {
    console.error('[CampaignService] Failed to publish campaign update message:', error);
    // Don't throw error to avoid breaking the campaign update flow
  }

  return response.data;
};

const getConsecutiveTsNumber = async (trafficId: string): Promise<number> => {
  const response = await Legacy.get(`${CAMPAIGNS_ENDPOINT}/${trafficId}/consecutive`);

  return response.data.next_consecutive_in_traffic_source;
};

// async function listLight(params = {}) {
//   const response = await API_V2.get('', {
//     params: {
//       r: `${Math.random()}`,
//       light: true,
//       ...params,
//     },
//   });

//   const fields = get(params, 'fields', null);

//   if (isString(fields) && fields.includes('trafficSource') === false) {
//     alert('traffic source is not present in query string fields');
//     return [];
//   }

//   const campaigns = response.data.map((rawCampaign: ICampaign) => {
//     const campaign = mapCampaignName(rawCampaign);

//     set(campaign, 'media_buyer', {
//       ...campaign.media_buyer,
//       fullName: `${campaign.media_buyer?.firstName} ${campaign.media_buyer?.lastName}`,
//     });

//     return campaign;
//   });

//   return orderBy(campaigns, 'name');
// }

const listByCampaignURLParams = async (
  params = {
    campaigns: [] as string[],
    fields: '',
    withConcurrency: 2,
  },
) => {
  let campaignAds: IAd[] = [];

  await PromisePool.for(chunk(params.campaigns, 40))
    .withConcurrency(params.withConcurrency)
    .process(async (chunk) => {
      const response = await Legacy.get<IAd[]>(`/ads${CAMPAIGNS_ENDPOINT}/list`, {
        params: {
          fields: params.fields,
          campaigns: chunk.join(','),
          r: `${Math.random()}`,
        },
      });
      campaignAds = [...campaignAds, ...response.data];
    });

  return campaignAds;
};

const updatePlacements = async (campaign: ICampaign, skipBlocks: boolean = false) => {
  console.log('updatePlacements called for campaign:', campaign._id);
  const url = `/campaigns/${campaign._id}?target=placements&populate[]=automations.automation&populate[]=trafficSource${skipBlocks ? '&skipBlocks=true' : ''
    }`;
  const response = await API_V2.put(url, campaign);
  return response.data;
};

const getSingleCampaign = async ({
  trafficSourceId,
  accountId,
  id,
}: {
  trafficSourceId: string;
  accountId: string;
  id: string;
}) => {
  const response = await API_V2.get(`${CAMPAIGNS_ENDPOINT}/${trafficSourceId}/remote?account=${accountId}&id=${id}`);
  return response.data;
};

const getAllCampaignsByTS = async (trafficSourceId: string) => {
  const response = await API_V2.get(`${CAMPAIGNS_ENDPOINT}/${trafficSourceId}/basic`);
  return response.data;
};

const clone = async (data: ICampaign, params = {}) => {
  const response = await API_V2.post<ICampaign>(`${CAMPAIGNS_ENDPOINT}/clone`, {
    id: data._id,
    name: `${data.name} - CLONE`,
  });

  return { ...response.data, trafficSource: data.trafficSource };
};

const cpcUpdateProcess = async (process: ICpcUpdateProcess[]) => {
  const response = await API_V2.post('/campaigns/update-cpc', process);
  return response.data;
};

const getReportByCampaignId = async ({ campaignId, target, dateRange, type, assetGroupId }: { campaignId?: string, target?: string, dateRange: { from: string, to: string, moment_name: string }, type?: string, assetGroupId?: string }) => {
  console.log('getReportByCampaignId', { campaignId, target, dateRange, type, assetGroupId });
  const response = await API_V2.post(`/campaigns/${campaignId}/reports`, {
    target,
    dateRange,
    type,
    assetGroupId
  });
  return response.data;
};

export const CampaignService = {
  conversionsUpdate,
  list,
  getById,
  update,
  updateByAPIV2,
  create,
  clone,
  getConsecutiveTsNumber,
  // listLight,
  listByCampaignURLParams,
  updatePlacements,
  getSingleCampaign,
  getAllCampaignsByTS,
  cpcUpdateProcess,
  getReportByCampaignId,
};
